import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Star, X, UploadCloud } from "lucide-react";
import api, { API, formatApiError } from "../../lib/api";
import { PROJECT_CATEGORIES } from "../../constants/content";

const EMPTY = { name: "", location: "", category: "renovations", description: "", scopeText: "", published: true, order: 0 };
const inputCls = "w-full border border-line bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold";
const labelCls = "block font-mono text-[10px] tracking-[0.18em] uppercase text-slate2 mb-1.5";

export default function ProjectsTab() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // null | {id?...form}
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/projects").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ ...EMPTY });
  const openEdit = (p) => setEditing({ ...p, scopeText: (p.scope || []).join("\n") });

  const save = async () => {
    if (!editing.name.trim()) { toast.error("Project name is required"); return; }
    setSaving(true);
    const payload = {
      name: editing.name.trim(), location: editing.location.trim(), category: editing.category,
      description: editing.description, scope: editing.scopeText.split("\n").map((s) => s.trim()).filter(Boolean),
      published: !!editing.published, order: Number(editing.order) || 0,
    };
    try {
      if (editing.id) {
        await api.put(`/admin/projects/${editing.id}`, payload);
        toast.success("Project updated");
      } else {
        const r = await api.post("/admin/projects", payload);
        setEditing({ ...editing, id: r.data.id, photos: [], cover: null });
        toast.success("Project created — now add photos below");
      }
      await load();
      if (editing.id) setEditing(null);
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this project and its photos from the website?")) return;
    try {
      await api.delete(`/admin/projects/${id}`);
      setItems(items.filter((x) => x.id !== id));
      if (editing?.id === id) setEditing(null);
      toast.success("Project deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (items === null) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>;

  const current = editing?.id ? items.find((x) => x.id === editing.id) : null;

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display font-bold text-xl text-ink">Projects <span className="text-slate2 text-sm font-sans font-normal">({items.length})</span></h2>
        {!editing && (
          <button onClick={openNew} data-testid="project-new-btn" className="inline-flex items-center gap-2 bg-ink text-white font-mono text-[10px] tracking-[0.15em] uppercase px-4 py-2.5 hover:bg-charcoal transition-colors">
            <Plus className="w-4 h-4 text-gold" /> New Project
          </button>
        )}
      </div>

      {editing && (
        <div data-testid="project-editor" className="bg-white border border-line p-5 md:p-7 mb-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-display font-semibold text-lg text-ink">{editing.id ? "Edit Project" : "New Project"}</h3>
            <button onClick={() => setEditing(null)} data-testid="project-editor-close" className="p-1.5 text-slate2 hover:text-ink"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Project Name*</label>
              <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="project-name-input" className={inputCls} placeholder="e.g. Karori Whole-House Renovation" />
            </div>
            <div>
              <label className={labelCls}>Location / Suburb</label>
              <input value={editing.location} onChange={(e) => setEditing({ ...editing, location: e.target.value })} data-testid="project-location-input" className={inputCls} placeholder="e.g. Lower Hutt" />
            </div>
            <div>
              <label className={labelCls}>Project Type</label>
              <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="project-category-select" className={inputCls}>
                {PROJECT_CATEGORIES.filter((c) => c.slug !== "all").map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Display Order (lower = first)</label>
              <input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} data-testid="project-order-input" className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Project Description</label>
              <textarea rows={4} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="project-description-input" className={`${inputCls} resize-y`} placeholder="What the client wanted, what was involved, how it turned out." />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Scope of Work (one item per line)</label>
              <textarea rows={3} value={editing.scopeText} onChange={(e) => setEditing({ ...editing, scopeText: e.target.value })} data-testid="project-scope-input" className={`${inputCls} resize-y`} placeholder={"Full kitchen and bathroom renovation\nNew deck and outdoor steps\nRe-piled rear of house"} />
            </div>
            <label className="sm:col-span-2 inline-flex items-center gap-2.5 text-sm text-ink cursor-pointer">
              <input type="checkbox" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} data-testid="project-published-checkbox" className="w-4 h-4 accent-[#C88E38]" />
              Published (visible on the website)
            </label>
          </div>
          <button onClick={save} disabled={saving} data-testid="project-save-btn" className="mt-5 inline-flex items-center gap-2 bg-gold hover:bg-golddark text-white font-mono text-[11px] tracking-[0.15em] uppercase px-6 py-3 disabled:opacity-60 transition-colors">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} {editing.id ? "Save Changes" : "Create Project"}
          </button>

          {current && <PhotoManager project={current} onChanged={load} />}
        </div>
      )}

      {items.length === 0 && !editing ? (
        <div data-testid="projects-admin-empty" className="bg-white border border-line px-8 py-16 text-center">
          <UploadCloud className="w-10 h-10 text-line mx-auto mb-4" />
          <p className="font-display font-semibold text-lg text-ink">No projects yet</p>
          <p className="text-sm text-slate2 mt-2">Add your first project with before, during and completed photos.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-admin-list">
          {items.map((p) => {
            const cover = p.photos?.find((x) => x.id === p.cover) || p.photos?.[0];
            return (
              <div key={p.id} className="bg-white border border-line" data-testid={`admin-project-${p.id}`}>
                <div className="aspect-[4/3] bg-charcoal relative overflow-hidden">
                  {cover && <img src={`${API}/media/${p.id}/${cover.id}`} alt={p.name} className="w-full h-full object-cover" />}
                  {!p.published && <span className="absolute top-2 left-2 bg-orange-500 text-white font-mono text-[9px] tracking-[0.15em] uppercase px-2 py-1">Draft</span>}
                </div>
                <div className="p-4">
                  <p className="font-display font-semibold text-sm text-ink truncate">{p.name}</p>
                  <p className="text-xs text-slate2 mt-0.5">{p.location || "—"} · {p.photos?.length || 0} photos</p>
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => openEdit(p)} data-testid={`admin-project-edit-${p.id}`} className="inline-flex items-center gap-1.5 text-xs border border-line px-3 py-1.5 hover:border-ink transition-colors"><Pencil className="w-3 h-3" /> Edit</button>
                    <button onClick={() => remove(p.id)} data-testid={`admin-project-delete-${p.id}`} className="inline-flex items-center gap-1.5 text-xs text-red-600 border border-line px-3 py-1.5 hover:border-red-300 transition-colors"><Trash2 className="w-3 h-3" /> Delete</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PhotoManager({ project, onChanged }) {
  const [stage, setStage] = useState("completed");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("stage", stage);
        fd.append("file", f);
        await api.post(`/admin/projects/${project.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Photo(s) uploaded");
      await onChanged();
    } catch (e) { toast.error(formatApiError(e)); } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const del = async (photoId) => {
    try {
      await api.delete(`/admin/projects/${project.id}/photos/${photoId}`);
      await onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setCover = async (photoId) => {
    try {
      await api.patch(`/admin/projects/${project.id}/cover`, { photo_id: photoId });
      toast.success("Cover photo set");
      await onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mt-8 pt-6 border-t border-line" data-testid="photo-manager">
      <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate2 mb-3">Project Photos (before / during / completed)</p>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select value={stage} onChange={(e) => setStage(e.target.value)} data-testid="photo-stage-select" className="border border-line px-3 py-2 text-sm bg-white focus:outline-none focus:border-gold">
          <option value="before">Before</option>
          <option value="during">During Construction</option>
          <option value="completed">Completed</option>
        </select>
        <button onClick={() => inputRef.current?.click()} disabled={busy} data-testid="photo-upload-btn" className="inline-flex items-center gap-2 bg-ink text-white font-mono text-[10px] tracking-[0.15em] uppercase px-4 py-2.5 hover:bg-charcoal disabled:opacity-60 transition-colors">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4 text-gold" />} Add Photos
        </button>
        <input ref={inputRef} type="file" multiple accept=".jpg,.jpeg,.png,.webp" className="hidden" data-testid="photo-file-input" onChange={(e) => upload(e.target.files)} />
        <span className="text-xs text-slate2/70">JPG, PNG or WEBP — automatically resized &amp; optimised</span>
      </div>
      {project.photos?.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {project.photos.map((ph) => (
            <div key={ph.id} className="border border-line bg-white" data-testid={`photo-item-${ph.id}`}>
              <div className="relative group">
                <img src={`${API}/media/${project.id}/${ph.id}`} alt={ph.caption || `${ph.stage} photo`} className="w-full aspect-[4/3] object-cover" />
                <div className="absolute inset-x-0 bottom-0 bg-ink/85 px-2.5 py-1.5 flex items-center justify-between">
                  <span className="font-mono text-[9px] tracking-[0.15em] uppercase text-white/80">{ph.stage}</span>
                  <div className="flex gap-1.5">
                    <button onClick={() => setCover(ph.id)} data-testid={`photo-cover-${ph.id}`} title="Set as cover" className={`p-1 ${project.cover === ph.id ? "text-gold" : "text-white/50 hover:text-gold"}`}>
                      <Star className={`w-3.5 h-3.5 ${project.cover === ph.id ? "fill-gold" : ""}`} />
                    </button>
                    <button onClick={() => del(ph.id)} data-testid={`photo-delete-${ph.id}`} title="Delete photo" className="p-1 text-white/50 hover:text-red-400">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
              <input
                defaultValue={ph.caption || ""}
                placeholder="Caption (optional) — shown under the photo"
                maxLength={200}
                data-testid={`photo-caption-${ph.id}`}
                className="w-full text-xs border-t border-line px-2.5 py-2 text-ink placeholder:text-slate2/50 focus:outline-none focus:bg-gold/5"
                onBlur={async (e) => {
                  const caption = e.target.value.trim();
                  if (caption === (ph.caption || "")) return;
                  try {
                    await api.patch(`/admin/projects/${project.id}/photos/${ph.id}`, { caption });
                    toast.success("Caption saved");
                    await onChanged();
                  } catch (err) { toast.error(formatApiError(err)); }
                }}
              />
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-slate2 border border-dashed border-line px-4 py-6 text-center">No photos yet — add before, during and completed shots.</p>
      )}
    </div>
  );
}
